import React, { useState, useEffect, useCallback, useRef } from 'react';
import { activityFeedApi } from '../../services/api';
import { adminApi } from '../../services/api';
import { StatusBadge, MetricCard, SectionHeader } from './ui/DesignSystem';
import {
  Search, Bell, LayoutList, ChevronDown, ChevronRight,
  CreditCard, RefreshCw, Upload, UserCheck, Plus, CheckCircle,
  XCircle, Edit, MessageSquare, Receipt, FileText, PenTool,
  Clock, Loader2, ShoppingCart, AlertTriangle, Eye, ExternalLink
} from 'lucide-react';

const ICON_MAP = { CreditCard, RefreshCw, Upload, UserCheck, Plus, CheckCircle, XCircle, Edit, MessageSquare, Receipt, FileText, PenTool };

const ACTIVITY_LABELS = {
  payment_received: 'Payment Received', status_changed: 'Status Changed',
  document_uploaded: 'Document Uploaded', client_document_uploaded: 'Client Upload',
  assignment_changed: 'Assignment', order_created: 'Order Created',
  order_completed: 'Completed', order_cancelled: 'Cancelled',
  internal_record_updated: 'Record Updated', note_added: 'Note Added',
  invoice_sent: 'Invoice Sent', govt_filing_updated: 'Filing Updated',
  signature_uploaded: 'Signature',
};

const ICON_COLOR_MAP = {
  payment_received: 'green', order_completed: 'green',
  status_changed: 'blue', assignment_changed: 'purple',
  client_document_uploaded: 'amber', document_uploaded: 'amber',
  note_added: 'blue', order_created: 'green',
};

const STATUS_CLS = {
  draft: 'draft', invoice_requested: 'pending', payment_pending: 'due',
  payment_received: 'paid', documents_pending: 'docs', under_review: 'review',
  signature_pending: 'progress', signature_review: 'progress',
  completed: 'complete', cancelled: 'cancelled',
};

// ── Red Alert Banner (unreviewed client uploads) ──────────
const ReviewAlertBanner = ({ onViewOrder }) => {
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await activityFeedApi.getReviewAlerts();
        setAlerts(data.alerts || []);
        setTotal(data.total || 0);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (total === 0) return null;

  const PREVIEW = 4;
  const visible = expanded ? alerts : alerts.slice(0, PREVIEW);
  const hasMore = total > PREVIEW;

  return (
    <div data-testid="review-alerts-banner" style={{ marginBottom: 16 }}>
      <div style={{
        padding: '14px 18px',
        background: 'var(--admin-red-soft)',
        border: '1px solid rgba(248,113,113,0.3)',
        borderRadius: 'var(--admin-radius)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <AlertTriangle style={{ width: 16, height: 16, color: 'var(--admin-red)' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--admin-red)' }}>
            {total} Order{total !== 1 ? 's' : ''} — Client Documents Awaiting Review
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visible.map(a => (
            <div
              key={a.order_id}
              onClick={() => onViewOrder(a.order_id)}
              className="admin-list-item"
              style={{ background: 'rgba(248,113,113,0.08)', cursor: 'pointer' }}
              data-testid={`review-alert-${a.order_id}`}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)' }}>{a.company_name}</span>
                  <span style={{ color: 'var(--admin-text-soft)', fontSize: 11 }}>&middot;</span>
                  <span style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>{a.product_name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                  <span style={{ fontSize: 12, color: 'var(--admin-red)', fontWeight: 600 }}>
                    {a.pending_count} doc{a.pending_count !== 1 ? 's' : ''} pending review
                  </span>
                  <span style={{
                    fontSize: 11, display: 'flex', alignItems: 'center', gap: 3, fontWeight: 600,
                    color: a.urgency === 'red' ? 'var(--admin-red)' : a.urgency === 'amber' ? 'var(--admin-amber)' : 'var(--admin-text-soft)',
                  }}>
                    <Clock style={{ width: 11, height: 11 }} />
                    {a.waiting_days > 0 ? `${a.waiting_days}d` : `${a.waiting_hours}h`}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--admin-text-soft)' }}>
                    &middot; {a.team_member_name || 'Unassigned'}
                  </span>
                </div>
              </div>
              <ExternalLink style={{ width: 14, height: 14, color: 'var(--admin-text-soft)', flexShrink: 0 }} />
            </div>
          ))}
        </div>
        {hasMore && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{ background: 'none', border: 'none', color: 'var(--admin-red)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '8px 0 0', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}
            data-testid="review-alerts-toggle"
          >
            {expanded ? 'Show less' : `View all ${total} alerts`}
            <ChevronDown style={{ width: 12, height: 12, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
          </button>
        )}
      </div>
    </div>
  );
};

// ── Notification Card ─────────────────────────────────────
const NotifCard = ({ item, onClick }) => {
  const IconComp = ICON_MAP[item.icon] || Bell;
  const iconCls = ICON_COLOR_MAP[item.activity_type] || 'blue';
  return (
    <div className="admin-notif" onClick={() => onClick(item.order_id)} data-testid={`notification-card-${item.id}`}>
      <div className={`admin-notif-icon ${iconCls}`}><IconComp /></div>
      <div className="admin-notif-body">
        <div className="admin-notif-type">{ACTIVITY_LABELS[item.activity_type] || item.activity_type}</div>
        <div className="admin-notif-row">
          <div className="admin-notif-title">{item.description}</div>
          <span className="admin-notif-time">{item.time_ago}</span>
        </div>
        <div className="admin-notif-meta">
          <span>{item.company_name}</span>
          {item.product_name && item.product_name !== 'Unknown' && (
            <><span className="sep">&middot;</span><span>{item.product_name}</span></>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Product Group (table with View All + inline team assign) ──
const PREVIEW_LIMIT = 5;

const TeamCell = ({ order, teamMembers, onAssign }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const name = order.team_member_name;

  const handleAssign = async (adminId) => {
    setSaving(true);
    try {
      await onAssign(order.id, adminId || null);
      setEditing(false);
    } catch {} finally { setSaving(false); }
  };

  if (editing) {
    return (
      <td onClick={e => e.stopPropagation()} style={{ minWidth: 140 }}>
        <select
          className="admin-select"
          style={{ fontSize: 12, padding: '4px 24px 4px 8px', height: 28 }}
          defaultValue={order.assigned_to || ''}
          onChange={e => handleAssign(e.target.value)}
          autoFocus
          onBlur={() => !saving && setEditing(false)}
          data-testid={`assign-select-${order.id}`}
        >
          <option value="">Unassigned</option>
          {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </td>
    );
  }

  return (
    <td
      onClick={e => { e.stopPropagation(); setEditing(true); }}
      style={{ cursor: 'pointer', minWidth: 140 }}
      title="Click to assign"
      data-testid={`team-cell-${order.id}`}
    >
      <span style={{ color: name ? 'var(--admin-text-dim)' : 'var(--admin-text-soft)', fontStyle: name ? 'normal' : 'italic', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
        {name || 'Unassigned'}
        <Edit style={{ width: 11, height: 11, opacity: 0.4 }} />
      </span>
    </td>
  );
};

const ProductGroup = ({ section, onOrderClick, teamMembers, onAssignTeam }) => {
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const total = section.orders_count || section.orders.length;
  const hasMore = total > PREVIEW_LIMIT;
  const visibleOrders = showAll ? section.orders : section.orders.slice(0, PREVIEW_LIMIT);

  return (
    <div className="admin-product-group">
      <div className="admin-product-head" onClick={() => setOpen(o => !o)} data-testid={`product-group-${section.product_id}`}>
        <div className="admin-product-name">
          {open ? <ChevronDown style={{ width: 14, height: 14 }} /> : <ChevronRight style={{ width: 14, height: 14 }} />}
          {section.product_name}
        </div>
        <span className="admin-product-count">{total}</span>
      </div>
      {open && (
        <>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Client</th>
                <th>Team</th>
                <th className="right">Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map(order => (
                <tr key={order.id} onClick={() => onOrderClick(order.id)} data-testid={`order-row-${order.id}`}>
                  <td className="cell-id">{order.id.slice(0, 8)}</td>
                  <td className="cell-client">{order.company_name}</td>
                  <TeamCell order={order} teamMembers={teamMembers} onAssign={onAssignTeam} />
                  <td className="cell-amount">{typeof order.amount === 'number' ? `\u20B9${order.amount.toLocaleString('en-IN')}` : '\u2014'}</td>
                  <td><StatusBadge status={order.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && !showAll && (
            <div className="admin-view-all-row" data-testid={`view-all-${section.product_id}`}>
              <span style={{ fontSize: 13, color: 'var(--admin-text-soft)' }}>
                Showing {PREVIEW_LIMIT} of {total} orders
              </span>
              <button className="admin-view-all-link" onClick={(e) => { e.stopPropagation(); setShowAll(true); }}>
                View all {total} orders
                <ChevronRight style={{ width: 14, height: 14 }} />
              </button>
            </div>
          )}
          {hasMore && showAll && (
            <div className="admin-view-all-row">
              <span style={{ fontSize: 13, color: 'var(--admin-text-soft)' }}>
                Showing all {total} orders
              </span>
              <button className="admin-view-all-link" onClick={(e) => { e.stopPropagation(); setShowAll(false); }} style={{ color: 'var(--admin-text-dim)' }}>
                Collapse
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Filter Select ─────────────────────────────────────────
const FilterSelect = ({ label, value, options, onChange, testId }) => (
  <select className="admin-filter" value={value || ''} onChange={e => onChange(e.target.value || null)} data-testid={testId}>
    <option value="">{label}</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// ── Main Component ────────────────────────────────────────
export const OrdersTab = ({ onViewOrderDetail, currentUser }) => {
  const [tab, setTab] = useState('notifications');
  const [filters, setFilters] = useState({ search: null, team_member_id: null, product_id: null, activity_type: null, status: null });
  // Raw search input (immediate) vs debounced value applied to filters
  const [searchInput, setSearchInput] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [notifPage, setNotifPage] = useState(1);
  const [notifHasMore, setNotifHasMore] = useState(false);
  const [notifTotal, setNotifTotal] = useState(0);
  const [ordersData, setOrdersData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  // Abort controllers — cancel previous in-flight request before firing a new one
  const ordersAbortRef = useRef(null);
  const notifAbortRef = useRef(null);

  // 300ms debounce: only apply searchInput to filters.search after typing pauses
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => (f.search === (searchInput || null) ? f : { ...f, search: searchInput || null }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    (async () => {
      try {
        const [tm, pr] = await Promise.all([activityFeedApi.getTeamMembers(), activityFeedApi.getProducts()]);
        setTeamMembers(tm); setProducts(pr);
      } catch {}
    })();
  }, []);

  const fetchNotifications = useCallback(async (page = 1, append = false) => {
    if (page === 1) setLoading(true); else setLoadingMore(true);
    // Cancel previous in-flight notifications request
    if (notifAbortRef.current) notifAbortRef.current.abort();
    const controller = new AbortController();
    notifAbortRef.current = controller;
    try {
      const p = { page, limit: 20 };
      if (filters.team_member_id) p.team_member_id = filters.team_member_id;
      if (filters.product_type) p.product_type = filters.product_type;
      if (filters.activity_type) p.activity_type = filters.activity_type;
      if (filters.search) p.search = filters.search;
      const resp = await activityFeedApi.getFeed(p, { signal: controller.signal });
      setNotifications(prev => append ? [...prev, ...resp.data] : resp.data);
      setNotifHasMore(resp.pagination.has_more);
      setNotifTotal(resp.pagination.total);
      setNotifPage(page);
    } catch (err) {
      // Ignore cancellations — they're expected when user keeps typing
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
    } finally {
      // Only stop loading if THIS controller is still the current one (i.e. not superseded)
      if (notifAbortRef.current === controller) { setLoading(false); setLoadingMore(false); }
    }
  }, [filters]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    // Cancel previous in-flight orders request
    if (ordersAbortRef.current) ordersAbortRef.current.abort();
    const controller = new AbortController();
    ordersAbortRef.current = controller;
    try {
      const p = {};
      if (filters.team_member_id) p.team_member_id = filters.team_member_id;
      if (filters.product_id) p.product_id = filters.product_id;
      if (filters.status) p.status = filters.status;
      else p.exclude_status = 'completed,cancelled';
      if (filters.search) p.search = filters.search;
      const data = await activityFeedApi.getOrdersGrouped(p, { signal: controller.signal });
      // Only commit if this is still the latest request
      if (ordersAbortRef.current === controller) setOrdersData(data);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
    } finally {
      if (ordersAbortRef.current === controller) setLoading(false);
    }
  }, [filters]);

  useEffect(() => { if (tab === 'notifications') fetchNotifications(1); else fetchOrders(); }, [tab, filters, fetchNotifications, fetchOrders]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || tab !== 'notifications' || !notifHasMore || loadingMore) return;
    const el = scrollRef.current;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) fetchNotifications(notifPage + 1, true);
  }, [tab, notifHasMore, loadingMore, notifPage, fetchNotifications]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) { el.addEventListener('scroll', handleScroll); return () => el.removeEventListener('scroll', handleScroll); }
  }, [handleScroll]);

  const hasFilters = Object.values(filters).some(v => v != null && v !== '');
  const clearFilters = () => { setSearchInput(''); setFilters({ search: null, team_member_id: null, product_id: null, activity_type: null, status: null }); };
  const cCount = ordersData?.compliance?.total || 0;
  const oCount = ordersData?.one_time?.total || 0;
  const uCount = ordersData?.uncategorized?.total || 0;
  const totalOrders = cCount + oCount + uCount;

  // Flatten all product sections into a single list
  const allSections = [];
  if (ordersData?.compliance?.sections) allSections.push(...ordersData.compliance.sections);
  if (ordersData?.one_time?.sections) allSections.push(...ordersData.one_time.sections);
  if (ordersData?.uncategorized?.sections) allSections.push(...ordersData.uncategorized.sections);
  allSections.sort((a, b) => a.product_name.localeCompare(b.product_name));

  const handleAssignTeam = async (orderId, adminId) => {
    try {
      await adminApi.assignOrder(orderId, adminId);
      fetchOrders(); // refresh
    } catch {}
  };

  const statuses = [
    { value: 'draft', label: 'Draft' },
    { value: 'invoice_requested', label: 'Invoice Requested' },
    { value: 'payment_pending', label: 'Payment Pending' },
    { value: 'payment_received', label: 'Payment Received' },
    { value: 'documents_pending', label: 'Documents Pending' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'signature_pending', label: 'Signature Pending' },
    { value: 'signature_review', label: 'Signature Review' },
    { value: 'pending_client_confirmation', label: 'Pending Client Confirmation' },
    { value: 'awaiting_tax_payment', label: 'Awaiting Tax Payment' },
    { value: 'needs_revision', label: 'Needs Revision' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];
  const activityTypes = [
    { value: 'payment_received', label: 'Payment Received' }, { value: 'status_changed', label: 'Status Changed' },
    { value: 'document_uploaded', label: 'Document Uploaded' }, { value: 'client_document_uploaded', label: 'Client Upload' },
    { value: 'assignment_changed', label: 'Assignment' }, { value: 'order_created', label: 'Order Created' },
    { value: 'order_completed', label: 'Completed' }, { value: 'note_added', label: 'Note Added' },
  ];

  return (
    <div data-testid="orders-dashboard">
      {/* Tabs */}
      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'notifications' ? 'active' : ''}`} onClick={() => setTab('notifications')} data-testid="tab-notifications">
          <Bell style={{ width: 16, height: 16 }} /> Notifications
          {notifTotal > 0 && <span className="admin-tab-count">{notifTotal}</span>}
        </button>
        <button className={`admin-tab ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')} data-testid="tab-orders">
          <LayoutList style={{ width: 16, height: 16 }} /> Orders by Type
          {totalOrders > 0 && <span className="admin-tab-count">{totalOrders}</span>}
        </button>
      </div>

      {/* Filters */}
      <div className="admin-filter-row" data-testid="filter-bar">
        <div style={{ position: 'relative', flex: '1 1 180px', maxWidth: 300 }}>
          <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--admin-text-soft)' }} />
          <input
            className="admin-input" style={{ paddingLeft: 32 }}
            placeholder="Search orders, clients, products..." value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            data-testid="filter-search"
          />
        </div>
        <FilterSelect testId="filter-team-member" label="Team Member" value={filters.team_member_id}
          options={[{ value: '__unassigned__', label: 'Unassigned' }, ...teamMembers.map(m => ({ value: m.id, label: m.name }))]}
          onChange={v => setFilters(f => ({ ...f, team_member_id: v }))} />
        <FilterSelect testId="filter-product" label="Product" value={filters.product_id}
          options={products.map(p => ({ value: p.id, label: p.name }))}
          onChange={v => setFilters(f => ({ ...f, product_id: v }))} />
        {tab === 'notifications' && (
          <FilterSelect testId="filter-activity-type" label="Activity Type" value={filters.activity_type}
            options={activityTypes} onChange={v => setFilters(f => ({ ...f, activity_type: v }))} />
        )}
        {tab === 'orders' && (
          <FilterSelect testId="filter-status" label="All (Active)" value={filters.status}
            options={statuses} onChange={v => setFilters(f => ({ ...f, status: v }))} />
        )}
        {hasFilters && <button className="admin-filter-clear" onClick={clearFilters} data-testid="filter-clear-all">Clear All</button>}
      </div>

      {/* Content */}
      <div ref={scrollRef} style={{ maxHeight: 'calc(100vh - 340px)', overflowY: 'auto', padding: '20px 0' }}>
        {/* Notifications */}
        {tab === 'notifications' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 4px' }}>
            {/* Red Alert: Unreviewed Client Uploads */}
            <ReviewAlertBanner onViewOrder={id => onViewOrderDetail && onViewOrderDetail(id)} />
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="admin-notif" style={{ opacity: 0.4 }}>
                  <div className="admin-skeleton" style={{ width: 38, height: 38, borderRadius: 8 }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className="admin-skeleton" style={{ width: '40%', height: 10 }} />
                    <div className="admin-skeleton" style={{ width: '75%', height: 14 }} />
                    <div className="admin-skeleton" style={{ width: '50%', height: 10 }} />
                  </div>
                </div>
              ))
            ) : notifications.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--admin-text-soft)' }}>
                <Bell style={{ width: 40, height: 40, margin: '0 auto 12px', opacity: 0.3 }} />
                <p style={{ fontSize: 14 }}>No activity found</p>
              </div>
            ) : (
              <>
                {notifications.map(item => <NotifCard key={item.id} item={item} onClick={id => onViewOrderDetail && onViewOrderDetail(id)} />)}
                {loadingMore && (
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Loader2 style={{ width: 20, height: 20, animation: 'spin 1s linear infinite', color: 'var(--admin-green)' }} />
                  </div>
                )}
                {!notifHasMore && notifications.length > 0 && (
                  <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--admin-text-soft)', padding: 16 }}>End of activity feed</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Orders by Type */}
        {tab === 'orders' && (
          <div style={{ padding: '0 4px' }}>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Array.from({ length: 4 }).map((_, i) => <div key={i} className="admin-skeleton" style={{ height: 48 }} />)}
              </div>
            ) : totalOrders === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--admin-text-soft)' }}>
                <ShoppingCart style={{ width: 40, height: 40, margin: '0 auto 12px', opacity: 0.3 }} />
                <p style={{ fontSize: 14 }}>No orders found</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {allSections.map(s => (
                  <ProductGroup key={s.product_id} section={s} onOrderClick={id => onViewOrderDetail(id)} teamMembers={teamMembers} onAssignTeam={handleAssignTeam} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
